import {observer} from 'mobx-react-lite'
import CastConnectedIcon from '@mui/icons-material/CastConnected'
import {makeOcStyles} from '@oc/styles'
import {Drawer, List, Typography, CircularProgress} from '@mui/material'
import {fullPath, useCustomerRouteContext, useUserPermissionsContext, useAuthInfo} from '@oc/shell-core'
import {LegalTextApproval} from '@oc/components'
import {CancelCircleIcon} from '@oc/icons'
import {ReadyKit} from '@oc/customer-v2'
import {Header, AccountSubHeader} from './Header'
import {DrawerItem, ReadyKitItem, CollapsibleItemSelectable} from './DrawerItem'
import {SelectedRkContextProvider} from '../../Contexts/SelectedRkContext'
import {ItemsWithCustomerAndRk} from './ItemsWithCustomerAndRk'
import routesObj from '../routes'
import {messages} from '../translations'
import {useIsAppDrawerExpanded} from '@oc/shell-core'

const useStyles = makeOcStyles(theme => ({
    root: {
        height: '100vh',
        width: 83
    },
    rootExpanded: {
        width: 280
    },
    drawerPaper: {
        position: 'relative'
    },
    accountSubheader: {
        lineHeight: '1rem',
        padding: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        backgroundColor: theme.syskronPalette.colors.P800,
        color: theme.syskronPalette.contrastColors.P800
    },
    subheader: {
        overflowWrap: 'anywhere'
    },
    subheaderIcon: {
        marginRight: theme.spacing(1)
    },
    list: {
        paddingTop: '1px',
        paddingBottom: theme.spacing(1),
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
            width: theme.spacing(1),
            backgroundColor: theme.palette.background.paper
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.syskronPalette.colors.S400,
            border: 'none',
            width: theme.spacing(1)
        }
    }
}))

function _AppDrawer() {
    const {classes, cx} = useStyles()
    const menuExpanded = useIsAppDrawerExpanded()
    const {username} = useAuthInfo()

    return (
        <Drawer
            id="app-drawer"
            variant="permanent"
            anchor="left"
            className={cx(classes.root, {
                [classes.rootExpanded]: menuExpanded
            })}
            classes={{
                paper: classes.drawerPaper
            }}
        >
            <Header />
            <LegalTextApproval
                userName={username}
                legalText={messages.getText('notices', 'legalText')}
                legalTextHeader={messages.getText('headers', 'legalTextHeader')}
                approval={messages.getText('confirmations', 'approval')}
                agree={messages.getText('buttons', 'agree')}
                disagree={messages.getText('buttons', 'disagree')}
            />
            <ItemsWithCustomerAndRk />
            <ItemsWithCustomer />
            <ItemsWithoutCustomer />
        </Drawer>
    )
}
export const AppDrawer = observer(_AppDrawer)

function _ItemsWithCustomer() {
    const {
        desiredAccountId,
        desiredReadyKit,
        readyKits,
        accountId,
        loadingCustomerLevel: loading
    } = useCustomerRouteContext()
    const userPermissions = useUserPermissionsContext()
    const {classes} = useStyles()

    const {withCustomerOnly: routes} = routesObj

    if (!desiredAccountId || (desiredAccountId && desiredReadyKit)) {
        return null
    }

    return (
        <>
            <AccountSubHeader />
            <List className={classes.list}>
                <DrawerItem route={fullPath(routes.customerEdit, routesObj)} />

                {userPermissions.accounts.canView(accountId) && (
                    <DrawerItem route={fullPath(routes.viewCustomer, routesObj)} />
                )}

                {userPermissions.equipment.canView(accountId) && (
                    <DrawerItem route={fullPath(routes.equipmentManagement, routesObj)} />
                )}

                {userPermissions.equipment.canView(accountId) && (
                    <DrawerItem route={fullPath(routes.viewEquipment, routesObj)} />
                )}

                {userPermissions.timestealers.canView(accountId) && (
                    <DrawerItem route={fullPath(routes.timestealerConfiguration, routesObj)} />
                )}

                {/* Temporarily removed, because sites backend doesn't work anyway.
                    Let's see how long it will take before someone asks us about it. */}
                {/*userPermissions.insight.canView(accountId) && (
                    <DrawerItem route={fullPath(routes.insight, routesObj)} />
                )*/}

                {userPermissions.rkMonitoring.canView(accountId) && (
                    <DrawerItem route={fullPath(routes.accountRkMonitoring, routesObj)} />
                )}
                {userPermissions.flashFluidFiles.canView() && (
                    <DrawerItem route={fullPath(routes.localFlashFluidFiles, routesObj)} />
                )}

                {userPermissions.fleetInfo.canView(accountId) && (
                    <DrawerItem route={fullPath(routes.fleetManagement, routesObj)} />
                )}

                <SelectedRkContextProvider>
                    <LinesAndReadyKits loading={loading} readyKits={readyKits} />
                </SelectedRkContextProvider>
            </List>
        </>
    )
}
const ItemsWithCustomer = observer(_ItemsWithCustomer)

interface LinesAndReadyKitsProps {
    readyKits: ReadyKit[]
    loading: boolean
}

function _LinesAndReadyKits({readyKits, loading}: LinesAndReadyKitsProps) {
    const menuExpanded = useIsAppDrawerExpanded()
    return (
        <CollapsibleItemSelectable
            icon={CastConnectedIcon}
            label={messages.getText('misc', 'lineReadykits')}
            idPrefix="menu-line-readykits"
            defaultExpanded={true}
            selectable={false}
        >
            {readyKits.map(({uuid, name}) => (
                <ReadyKitItem key={uuid} rkId={uuid} rkName={name} />
            ))}

            {readyKits.length < 1 ? (
                <Typography align="center">
                    <i>
                        {loading ? (
                            menuExpanded ? (
                                messages.getText('misc', 'loading')
                            ) : (
                                <CircularProgress size="24px" />
                            )
                        ) : menuExpanded ? (
                            messages.getText('misc', 'noRk')
                        ) : (
                            <CancelCircleIcon color="disabled" size="24px" />
                        )}
                    </i>
                </Typography>
            ) : null}
        </CollapsibleItemSelectable>
    )
}
const LinesAndReadyKits = observer(_LinesAndReadyKits)

function _ItemsWithoutCustomer() {
    const {desiredAccountId, desiredReadyKit, loadingCustomerLevel} = useCustomerRouteContext()
    const userPermissions = useUserPermissionsContext()
    const {classes} = useStyles()

    const routes = routesObj.allOther

    if (desiredAccountId || desiredReadyKit || loadingCustomerLevel) {
        return null
    }

    return (
        <List className={classes.list}>
            <DrawerItem route={fullPath(routes.dashboard, routesObj)} />
            <DrawerItem route={fullPath(routes.dashboardV1, routesObj)} />

            {userPermissions.rkMonitoring.canViewSome() && (
                <DrawerItem route={fullPath(routes.globalRkMonitoring, routesObj)} />
            )}
            {userPermissions.deployments.canViewSome() && <DrawerItem route={fullPath(routes.massDeploy, routesObj)} />}
            {userPermissions.auditLogs.canViewSome() && <DrawerItem route={fullPath(routes.audit, routesObj)} />}

            <DrawerItem route={fullPath(routes.globalSettings, routesObj)} />

            <DrawerItem route={fullPath(routes.userManagement, routesObj)} />
        </List>
    )
}
const ItemsWithoutCustomer = observer(_ItemsWithoutCustomer)
